import {
  collection,
  query,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

export class NotificationService {
  private static listeners: (() => void)[] = [];

  // Listen for new bookings and show notifications
  static startBookingNotifications() {
    const bookingsRef = collection(db, 'bookings');
    const q = query(
      bookingsRef,
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const booking = change.doc.data();
          this.showBookingNotification(booking);
        }
      });
    });

    this.listeners.push(unsubscribe);
    return unsubscribe;
  }

  // Listen for new routes and show notifications
  static startRouteNotifications() {
    const routesRef = collection(db, 'routes');
    const q = query(
      routesRef,
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const route = change.doc.data();
          this.showRouteNotification(route);
        }
      });
    });

    this.listeners.push(unsubscribe);
    return unsubscribe;
  }

  // Listen for new buses and show notifications
  static startBusNotifications() {
    const busesRef = collection(db, 'buses');
    const q = query(
      busesRef,
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const bus = change.doc.data();
          this.showBusNotification(bus);
        }
      });
    });

    this.listeners.push(unsubscribe);
    return unsubscribe;
  }

  // Show booking notification
  private static showBookingNotification(booking: any) {
    const passengerName = booking.passengerName || 'Unknown';
    const totalPrice = booking.totalPrice || 0;
    const status = booking.status || 'pending';

    toast.success(
      `New Booking: ${passengerName} - ₵${totalPrice} (${status})`,
      {
        duration: 5000,
        icon: '🎫',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      }
    );
  }

  // Show route notification
  private static showRouteNotification(route: any) {
    const fromCity = route.fromCity || 'Unknown';
    const toCity = route.toCity || 'Unknown';
    const price = route.price || 0;

    toast.success(
      `New Route: ${fromCity} → ${toCity} - ₵${price}`,
      {
        duration: 4000,
        icon: '🚌',
        style: {
          background: '#3B82F6',
          color: '#fff',
        },
      }
    );
  }

  // Show bus notification
  private static showBusNotification(bus: any) {
    const busNumber = bus.busNumber || 'Unknown';
    const operatorName = bus.operatorName || 'Unknown';

    toast.success(
      `New Bus Added: ${busNumber} - ${operatorName}`,
      {
        duration: 4000,
        icon: '🚐',
        style: {
          background: '#8B5CF6',
          color: '#fff',
        },
      }
    );
  }

  // Listen for new schedules and show notifications
  static startScheduleNotifications() {
    const schedulesRef = collection(db, 'schedules');
    const q = query(
      schedulesRef,
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const schedule = change.doc.data();
          const from = schedule.fromCity || 'Unknown';
          const to = schedule.toCity || 'Unknown';
          toast.success(
            `New Schedule: ${from} → ${to}`,
            {
              duration: 4000,
              icon: '📅',
              style: { background: '#F59E0B', color: '#fff' },
            }
          );
        }
      });
    });

    this.listeners.push(unsubscribe);
    return unsubscribe;
  }

  // Start all notifications
  static startAllNotifications() {
    this.startBookingNotifications();
    this.startRouteNotifications();
    this.startBusNotifications();
    this.startScheduleNotifications();

    console.log('🔔 All notifications started');
  }

  // Stop all notifications
  static stopAllNotifications() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners = [];
    console.log('🔕 All notifications stopped');
  }

  // Show custom notification
  static showCustomNotification(message: string, type: 'success' | 'error' | 'info' = 'info') {
    const config = {
      duration: 4000,
      style: {
        background: type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6',
        color: '#fff',
      },
    };

    if (type === 'success') {
      toast.success(message, config);
    } else if (type === 'error') {
      toast.error(message, config);
    } else {
      toast(message, config);
    }
  }
}
